import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCog, Calendar, Users, LogOut, Plus, Eye, Edit2, Trash2, BarChart3, Clock, CheckCircle, FileText, Search, Filter, Zap } from 'lucide-react';
import { useSmartRefresh } from '../hooks/useSmartRefresh';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { PhoneInput } from './ui/phone-input';
import { DateInput } from './ui/date-input';
import StatisticsTab from './StatisticsTab';
import DoctorLeaveManagement from './DoctorLeaveManagement';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateToDDMMYYYY, formatDateToYYYYMMDD, getTodayYYYYMMDD, getTodayDDMMYYYY, formatLeaveDates } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Webhook helper function - fire and forget
const sendWebhook = async (type, data) => {
  try {
    await axios.post(`${API}/appointments/webhook/${type}`, data, { timeout: 5000 });
  } catch (error) {
    console.error(`Webhook ${type} failed (non-blocking):`, error.message);
  }
};

const FrontDeskDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // Restore active tab from sessionStorage on page refresh
    return sessionStorage.getItem('frontdeskActiveTab') || 'appointments';
  });
  
  // Data states
  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [babies, setBabies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedDoctorFilter, setSelectedDoctorFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDateFilter, setSelectedDateFilter] = useState(() => {
    // Get current date in local timezone
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const currentDate = `${year}-${month}-${day}`;
    console.log(`🕐 Front Desk Dashboard initialized with date: ${currentDate} (${now.toLocaleString()})`);
    return currentDate;
  });
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('all'); // 'all' or 0-11 for months
  
  // Helper function to get today's date in YYYY-MM-DD format (for backend)
  const getTodayFormatted = getTodayYYYYMMDD;
  
  // Appointment form
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [showAppointmentReceipt, setShowAppointmentReceipt] = useState(false);
  const [bookingReceipt, setBookingReceipt] = useState(null);
  
  // Receipts tab filters
  const [receiptsSearchQuery, setReceiptsSearchQuery] = useState('');
  const [receiptsSelectedDate, setReceiptsSelectedDate] = useState(() => new Date().toISOString().split('T')[0]); // Default to today
  const [receiptsSelectedDoctor, setReceiptsSelectedDoctor] = useState('all'); // Filter by doctor
  const [receiptsFilterMode, setReceiptsFilterMode] = useState('date'); // 'date', 'range', 'month'
  const [receiptsDateFrom, setReceiptsDateFrom] = useState('');
  const [receiptsDateTo, setReceiptsDateTo] = useState('');
  const [receiptsSelectedMonth, setReceiptsSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [appointmentForm, setAppointmentForm] = useState(() => {
    return {
      baby_id: '',
      baby_name: '',
      baby_dob: '',
      baby_gender: 'Male',
      baby_phone: '',
      baby_country_code: '+91',
      doctor_id: '',
      appointment_date: getTodayFormatted(),
      appointment_time: '',
      notes: '',
      payment_method: 'Cash',
      appointment_type: 'new',  // new or followup
      followup_of_date: '',
      category: 'Regular',  // Regular, Known
      category_ref: ''  // Reference/clarification for Known
    };
  });
  const [useExistingBaby, setUseExistingBaby] = useState(true);
  const [foundBaby, setFoundBaby] = useState(null);
  const [lastVisitInfo, setLastVisitInfo] = useState(null);  // Info about last visit for free followup check
  
  // Time slots for dropdown
  const allTimeSlots = [
    '09:00 AM', '09:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '01:00 PM', '01:30 PM', '02:00 PM', '02:30 PM',
    '03:00 PM', '03:30 PM', '04:00 PM', '04:30 PM', '05:00 PM', '05:30 PM',
    '06:00 PM', '06:30 PM', '07:00 PM', '07:30 PM', '08:00 PM', '08:30 PM'
  ];

  // Helper function to convert time string to minutes
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    // Handle 24-hour format (no AM/PM)
    if (!period) {
      return hours * 60 + (minutes || 0);
    }
    
    // Handle 12-hour format with AM/PM
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + (minutes || 0);
  };

  // Get available time slots based on doctor schedule (allows multiple bookings)
  const getAvailableTimeSlots = (doctorId, appointmentDate) => {
    if (!doctorId || !appointmentDate) return allTimeSlots;

    const selectedDoctor = doctors.find(d => d.id === doctorId);
    if (!selectedDoctor) return allTimeSlots;

    // Check if appointment date is on doctor's leave
    if (selectedDoctor.leave_dates && selectedDoctor.leave_dates.length > 0) {
      const isOnLeave = selectedDoctor.leave_dates.includes(appointmentDate);
      if (isOnLeave) {
        return []; // Return empty array if doctor is on leave
      }
    }

    // Parse doctor schedule (format: "09:00 AM - 05:00 PM")
    let doctorStartTime = '09:00 AM';
    let doctorEndTime = '05:00 PM';
    
    if (selectedDoctor.schedule) {
      const scheduleMatch = selectedDoctor.schedule.match(/(\d{1,2}:\d{2}\s*[AP]M)\s*-\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
      if (scheduleMatch) {
        doctorStartTime = scheduleMatch[1].trim();
        doctorEndTime = scheduleMatch[2].trim();
      }
    }

    const startMinutes = timeToMinutes(doctorStartTime);
    const endMinutes = timeToMinutes(doctorEndTime);

    // Check if appointment date is today
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const isToday = appointmentDate === today;
    
    // Get current time in minutes
    let currentTimeMinutes = 0;
    if (isToday) {
      const now = new Date();
      currentTimeMinutes = now.getHours() * 60 + now.getMinutes();
    }

    // Filter slots within doctor's working hours only
    // If today, also filter out past times
    return allTimeSlots.filter(slot => {
      const slotMinutes = timeToMinutes(slot);
      const withinWorkingHours = slotMinutes >= startMinutes && slotMinutes <= endMinutes;
      
      // If appointment is for today, check if time is in the future
      if (isToday) {
        return withinWorkingHours && slotMinutes > currentTimeMinutes;
      }
      
      return withinWorkingHours;
    });
  };
  
  // Edit appointment
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    status: '',
    notes: '',
    appointment_date: '',
    appointment_time: ''
  });
  
  // Doctor schedule management
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [editingDoctorSchedule, setEditingDoctorSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    schedule: '',
    leave_dates: []
  });
  
  // Extend availability
  const [showExtendDialog, setShowExtendDialog] = useState(false);
  const [extendingDoctor, setExtendingDoctor] = useState(null);
  const [extendForm, setExtendForm] = useState({ schedule: '' });

  useEffect(() => {
    const userData = localStorage.getItem('healthcareUser');
    if (!userData) {
      navigate('/frontdesk-login');
      return;
    }
    
    const parsedUser = JSON.parse(userData);
    if (parsedUser.role !== 'frontdesk') {
      toast.error('Access denied. Front desk only.');
      navigate('/frontdesk-login');
      return;
    }
    
    setUser(parsedUser);
    
    // Load hospital data from localStorage
    const hospitalData = localStorage.getItem('currentHospital');
    if (hospitalData) {
      setHospital(JSON.parse(hospitalData));
    }
    
    loadData();
    
    // Function to refresh front desk staff info from database
    const refreshStaffInfo = async () => {
      try {
        const response = await axios.get(`${API}/front-desk-staff/${parsedUser.id}`);
        if (response.data) {
          const updatedUser = {
            ...parsedUser,
            name: response.data.name,
            email: response.data.email,
            phone: response.data.phone
          };
          
          // Update localStorage and state if data has changed
          const currentUser = JSON.parse(localStorage.getItem('healthcareUser'));
          if (JSON.stringify(currentUser) !== JSON.stringify(updatedUser)) {
            localStorage.setItem('healthcareUser', JSON.stringify(updatedUser));
            setUser(updatedUser);
          }
        }
      } catch (error) {
        console.error('Error refreshing staff info:', error);
      }
    };
    
    // Refresh staff info on initial load
    refreshStaffInfo();
    
    // Set up interval to refresh staff info periodically (background only)
    const staffInfoInterval = setInterval(() => {
      refreshStaffInfo();
    }, 300000); // 5 minutes
    
    // Cleanup interval on unmount
    return () => {
      clearInterval(staffInfoInterval);
    };
  }, [navigate]);

  // Auto-update to current date after midnight and reload appointments
  useEffect(() => {
    const checkDateChange = async () => {
      // Get current date in local timezone
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const currentDate = `${year}-${month}-${day}`;
      
      if (selectedDateFilter !== currentDate) {
        console.log(`🕐 Date changed from ${selectedDateFilter} to ${currentDate} (${now.toLocaleString()})`);
        console.log(`📅 Updating Front Desk dashboard to show ${currentDate} appointments...`);
        
        // First update the date
        setSelectedDateFilter(currentDate);
        
        // Then reload appointments to show new date's appointments
        await loadData();
        console.log(`✅ Front Desk dashboard updated - now showing ${currentDate} appointments`);
        toast.success(`Dashboard updated to ${formatDateToDDMMYYYY(currentDate)}`);
      }
    };

    // Check every minute if date has changed
    const interval = setInterval(checkDateChange, 60000); // Check every 60 seconds

    return () => clearInterval(interval);
  }, [selectedDateFilter]);

  // Real-time data synchronization - Poll for updates every 30 seconds
  useEffect(() => {
    const syncData = async () => {
      // Check if user is actively working (dialog open, input focused, or typing)
      const activeElement = document.activeElement;
      const isInputActive = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );
      
      // Check if any dialog/modal is open
      const isDialogOpen = document.querySelector('[role="dialog"]') !== null;
      
      // Skip sync if user is actively working
      if (isInputActive || isDialogOpen) {
        console.log('⏸️ Skipping sync - user is actively working');
        return;
      }
      
      try {
        // Get current hospital from user data
        const userData = localStorage.getItem('healthcareUser');
        const currentUser = userData ? JSON.parse(userData) : null;
        const currentHospitalId = currentUser?.hospital_id;

        if (!currentHospitalId) return;

        // Silently reload data in background
        const [doctorsRes, appointmentsRes, babiesRes] = await Promise.all([
          axios.get(`${API}/doctors`),
          axios.get(`${API}/appointments-detailed?hospital_id=${currentHospitalId}`),
          axios.get(`${API}/babies`)
        ]);

        // Filter doctors for current hospital
        const hospitalDoctors = doctorsRes.data.filter(d => 
          d.hospital_id === currentHospitalId || 
          (d.hospital_ids && d.hospital_ids.includes(currentHospitalId))
        );
        // Appointments are already filtered by hospital_id from backend
        const filteredAppointments = appointmentsRes.data;

        // Only update if data has actually changed
        if (JSON.stringify(filteredAppointments) !== JSON.stringify(appointments)) {
          setAppointments(filteredAppointments);
          console.log('🔄 Appointments updated in real-time');
        }

        if (JSON.stringify(hospitalDoctors) !== JSON.stringify(doctors)) {
          setDoctors(hospitalDoctors);
          console.log('🔄 Doctors data updated in real-time');
        }

        if (JSON.stringify(babiesRes.data) !== JSON.stringify(babies)) {
          setBabies(babiesRes.data);
          console.log('🔄 Babies data updated in real-time');
        }
      } catch (error) {
        console.error('Error syncing data:', error);
        // Silently fail - don't show error to user for background sync
      }
    };

    // Note: Smart auto-refresh is set up separately below
  }, [appointments, doctors, babies]);

  // Smart auto-refresh that pauses during user interactions
  useSmartRefresh(
    useCallback(() => {
      syncData();
    }, [appointments, doctors, babies]),
    30000, // 30 seconds
    3000   // Wait 3 seconds after user activity before resuming
  );

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get current hospital from user data
      const userData = localStorage.getItem('healthcareUser');
      const currentUser = userData ? JSON.parse(userData) : null;
      const currentHospitalId = currentUser?.hospital_id;
      
      // Retry logic for API calls
      const fetchWithRetry = async (url, retries = 3, delay = 1000) => {
        for (let i = 0; i < retries; i++) {
          try {
            const response = await axios.get(url, { timeout: 30000 });
            return response;
          } catch (error) {
            if (i === retries - 1) throw error;
            console.warn(`Retry ${i + 1}/${retries} for ${url}`);
            await new Promise(r => setTimeout(r, delay));
          }
        }
      };
      
      const [doctorsRes, appointmentsRes, babiesRes] = await Promise.all([
        fetchWithRetry(`${API}/doctors`),
        fetchWithRetry(`${API}/appointments-detailed${currentHospitalId ? `?hospital_id=${currentHospitalId}` : ''}`),
        fetchWithRetry(`${API}/babies`)
      ]);
      
      // Filter doctors by current hospital and deduplicate by ID
      const hospitalDoctors = currentHospitalId
        ? doctorsRes.data.filter(d => 
            d.hospital_id === currentHospitalId || 
            (d.hospital_ids && d.hospital_ids.includes(currentHospitalId))
          )
        : doctorsRes.data;
      
      // Deduplicate doctors by ID (keep first occurrence)
      const uniqueDoctors = [];
      const seenDoctorIds = new Set();
      for (const doctor of hospitalDoctors) {
        if (!seenDoctorIds.has(doctor.id)) {
          uniqueDoctors.push(doctor);
          seenDoctorIds.add(doctor.id);
        }
      }
      
      // Appointments are already filtered by hospital_id from backend
      const filteredAppointments = appointmentsRes.data;
      
      setDoctors(uniqueDoctors);
      setAppointments(filteredAppointments);
      setBabies(babiesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('healthcareUser');
    sessionStorage.removeItem('frontdeskActiveTab');
    toast.success('Logged out successfully');
    navigate('/frontdesk-login');
  };

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    sessionStorage.setItem('frontdeskActiveTab', tabName);
  };

  // Helper to normalize date for comparison (handles both YYYY-MM-DD and DD/MM/YYYY)
  const normalizeDateForComparison = (dateStr) => {
    if (!dateStr) return '';
    // If already in YYYY-MM-DD format, return as-is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    // Convert DD/MM/YYYY to YYYY-MM-DD
    if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = dateStr.split('/');
      return `${year}-${month}-${day}`;
    }
    return dateStr;
  };

  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    
    const birthDate = new Date(dob.split('/').reverse().join('-'));
    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    const parts = [];
    if (years > 0) {
      parts.push(`${years} year${years > 1 ? 's' : ''}`);
    }
    if (months > 0) {
      parts.push(`${months} month${months > 1 ? 's' : ''}`);
    }
    if (days > 0 || parts.length === 0) {
      parts.push(`${days} day${days !== 1 ? 's' : ''}`);
    }
    
    return parts.join(', ');
  };

  // Format date with ordinal suffix (e.g., "23rd October 2025")
  const formatDateWithOrdinal = (date) => {
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  };

  // Format selected date for display (DD/MM/YYYY to readable format)
  const formatSelectedDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return formatDateWithOrdinal(date);
  };

  // Handle DOB input with auto-advance
  const handleDobInput = (value, isNewBaby = false) => {
    let cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length >= 2) {
      let day = cleaned.substring(0, 2);
      if (parseInt(day) > 31) day = '31';
      // Always add "/" after day, even if nothing follows
      cleaned = day + '/' + (cleaned.length > 2 ? cleaned.substring(2) : '');
    }
    
    if (cleaned.length >= 5) {
      let month = cleaned.substring(3, 5);
      if (parseInt(month) > 12) month = '12';
      // Always add "/" after month, even if nothing follows
      cleaned = cleaned.substring(0, 3) + month + '/' + (cleaned.length > 5 ? cleaned.substring(5, 9) : '');
    }
    
    if (cleaned.length > 10) {
      cleaned = cleaned.substring(0, 10);
    }
    
    setAppointmentForm(prev => ({ ...prev, baby_dob: cleaned }));
  };

  // Generate calendar days for current month
  const generateCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const appointmentCount = appointments.filter(apt => apt.appointment_date === dateStr).length;
      days.push({ day, dateStr, appointmentCount });
    }
    
    return days;
  };

  // Navigate calendar month
  const navigateMonth = (direction) => {
    const newMonth = new Date(calendarMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCalendarMonth(newMonth);
  };

  // Calculate statistics for doctors
  const calculateDoctorStats = () => {
    const today = new Date().toISOString().split('T')[0];
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const isAllMonths = selectedMonth === 'all';

    return doctors.map(doctor => {
      const doctorAppointments = appointments.filter(apt => apt.doctor_id === doctor.id && apt.status === 'completed');
      
      // Filter by year and month if selected
      const filteredAppointments = doctorAppointments.filter(apt => {
        const aptDate = new Date(apt.appointment_date);
        const yearMatch = aptDate.getFullYear() === selectedYear;
        const monthMatch = isAllMonths || aptDate.getMonth() === parseInt(selectedMonth);
        return yearMatch && monthMatch;
      });
      
      // Daily cases (only for current year view and current month/today's date)
      const dailyCases = selectedYear === currentYear && (isAllMonths || parseInt(selectedMonth) === currentMonth)
        ? filteredAppointments.filter(apt => apt.appointment_date === today).length 
        : 0;
      
      // Monthly cases
      const monthlyCases = isAllMonths
        ? (selectedYear === currentYear ? doctorAppointments.filter(apt => {
            const aptDate = new Date(apt.appointment_date);
            return aptDate.getMonth() === currentMonth && aptDate.getFullYear() === currentYear;
          }).length : 0)
        : filteredAppointments.length;
      
      // Yearly cases (for selected year or selected period)
      const yearlyCases = isAllMonths
        ? doctorAppointments.filter(apt => {
            const aptDate = new Date(apt.appointment_date);
            return aptDate.getFullYear() === selectedYear;
          }).length
        : filteredAppointments.length;
      
      // Total cases
      const totalCases = doctorAppointments.length;
      
      return {
        doctor,
        dailyCases,
        monthlyCases,
        yearlyCases,
        totalCases
      };
    });
  };

  // Get available years from appointments
  const getAvailableYears = () => {
    const years = new Set();
    appointments.forEach(apt => {
      if (apt.appointment_date) {
        const year = new Date(apt.appointment_date).getFullYear();
        years.add(year);
      }
    });
    const yearArray = Array.from(years).sort((a, b) => b - a); // Sort descending
    if (yearArray.length === 0) yearArray.push(new Date().getFullYear()); // Add current year if no appointments
    return yearArray;
  };

  // Calculate overall statistics
  const calculateOverallStats = () => {
    const stats = calculateDoctorStats();
    return {
      dailyTotal: stats.reduce((sum, s) => sum + s.dailyCases, 0),
      monthlyTotal: stats.reduce((sum, s) => sum + s.monthlyCases, 0),
      yearlyTotal: stats.reduce((sum, s) => sum + s.yearlyCases, 0),
      allTimeTotal: stats.reduce((sum, s) => sum + s.totalCases, 0)
    };
  };

  // Search for baby by ID or Name+DOB
  const handleSearchBaby = async () => {
    try {
      if (appointmentForm.baby_id) {
        // Search by ID (can be short_id, full UUID, or UUID prefix)
        const searchId = appointmentForm.baby_id.trim();
        const baby = babies.find(b => 
          b.id === searchId || 
          (b.short_id && b.short_id.toUpperCase() === searchId.toUpperCase()) ||
          b.id.toLowerCase().startsWith(searchId.toLowerCase())
        );
        if (baby) {
          setFoundBaby(baby);
          // Display short ID in the input field
          setAppointmentForm(prev => ({ ...prev, baby_id: baby.short_id || baby.id.substring(0, 8).toUpperCase() }));
          toast.success(`Baby found: ${baby.name} (${baby.short_id || baby.id.substring(0, 8).toUpperCase()})`);
          // Fetch last visit info if doctor is selected
          if (appointmentForm.doctor_id) {
            fetchLastVisitInfo(baby.id, appointmentForm.doctor_id);
          }
        } else {
          setFoundBaby(null);
          setLastVisitInfo(null);
          toast.error('Baby not found with this ID');
        }
      } else if (appointmentForm.baby_name && appointmentForm.baby_dob) {
        // Search by Name and DOB
        const baby = babies.find(b => 
          b.name.toLowerCase() === appointmentForm.baby_name.toLowerCase() &&
          b.date_of_birth === appointmentForm.baby_dob
        );
        if (baby) {
          setFoundBaby(baby);
          // Display 8-digit ID to user but store full ID in foundBaby for appointment creation
          setAppointmentForm(prev => ({ ...prev, baby_id: baby.short_id || baby.id.substring(0, 8).toUpperCase() }));
          toast.success(`Baby found: ${baby.name} (${baby.short_id || baby.id.substring(0, 8).toUpperCase()})`);
          // Fetch last visit info if doctor is selected
          if (appointmentForm.doctor_id) {
            fetchLastVisitInfo(baby.id, appointmentForm.doctor_id);
          }
        } else {
          setFoundBaby(null);
          setLastVisitInfo(null);
          toast.error('Baby not found with this name and date of birth');
        }
      } else {
        toast.error('Please enter ID or both Name and Date of Birth');
      }
    } catch (error) {
      console.error('Error searching baby:', error);
      toast.error('Error searching for baby');
    }
  };

  // Fetch last visit info for a baby with a doctor to check for free follow-up
  const fetchLastVisitInfo = async (babyId, doctorId) => {
    try {
      const response = await axios.get(`${API}/appointments/last-visit/${babyId}/${doctorId}/${hospitalId}`);
      setLastVisitInfo(response.data);
    } catch (error) {
      console.error('Error fetching last visit info:', error);
      setLastVisitInfo(null);
    }
  };

  const handleCreateAppointment = async () => {
    try {
      if (!appointmentForm.doctor_id || !appointmentForm.appointment_date || !appointmentForm.appointment_time) {
        toast.error('Please fill all required fields');
        return;
      }

      if (!appointmentForm.baby_phone) {
        toast.error('Please provide phone number for appointment');
        return;
      }

      let finalBabyId = null;

      if (useExistingBaby) {
        // Use found baby or entered baby_id
        if (foundBaby) {
          finalBabyId = foundBaby.id;
        } else if (appointmentForm.baby_id) {
          // Verify baby exists (handle both full ID and 8-digit prefix)
          const searchId = appointmentForm.baby_id.toLowerCase();
          const baby = babies.find(b => 
            b.id === appointmentForm.baby_id || 
            b.id.toLowerCase().startsWith(searchId)
          );
          if (!baby) {
            toast.error('Baby not found. Please search first.');
            return;
          }
          finalBabyId = baby.id;
        } else {
          toast.error('Please search for a baby first');
          return;
        }
      } else {
        // Create new baby
        if (!appointmentForm.baby_name || !appointmentForm.baby_dob || !appointmentForm.baby_gender) {
          toast.error('Please provide all baby details');
          return;
        }

        // Check for duplicate baby before creating
        const duplicateCheck = await axios.post(`${API}/babies/check-duplicate`, {
          name: appointmentForm.baby_name,
          date_of_birth: appointmentForm.baby_dob
        });

        if (duplicateCheck.data.exists) {
          const existingBaby = duplicateCheck.data.baby;
          toast.error(`Already exists: ${existingBaby.name} (${existingBaby.date_of_birth}). Please use existing option.`);
          return;
        }

        // Create baby without phone (phone is only for appointment)
        const newBabyData = {
          name: appointmentForm.baby_name,
          date_of_birth: appointmentForm.baby_dob,
          gender: appointmentForm.baby_gender,
          height: 0.0,
          weight: 0.0,
          created_via: "appointment"
        };

        const babyResponse = await axios.post(`${API}/babies`, newBabyData);
        finalBabyId = babyResponse.data.id;
        
        // Initialize vaccination schedule for new baby (non-blocking - don't await)
        axios.post(`${API}/vaccinations/initialize/${finalBabyId}`)
          .catch(vaccError => console.error('Error initializing vaccinations:', vaccError));
      }

      if (!appointmentForm.payment_method && appointmentForm.category === 'Regular') {
        toast.error('Please select payment method');
        return;
      }

      // Validate category_ref for Known
      if (appointmentForm.category === 'Known' && !appointmentForm.category_ref.trim()) {
        toast.error('Please provide a reference for Known category');
        return;
      }

      // Create appointment with phone number (stored in notes)
      const appointmentData = {
        baby_id: finalBabyId,
        doctor_id: appointmentForm.doctor_id,
        hospital_id: user.hospital_id,  // Front desk user's hospital
        appointment_date: appointmentForm.appointment_date,
        appointment_time: appointmentForm.appointment_time,
        notes: appointmentForm.baby_phone, // Store phone in notes
        payment_method: appointmentForm.category === 'Regular' ? appointmentForm.payment_method : 'N/A',
        booking_source: 'front_desk',
        created_by: user.id,
        appointment_type: useExistingBaby ? appointmentForm.appointment_type : 'new',  // New babies are always new visits
        followup_of_date: appointmentForm.appointment_type === 'followup' ? (lastVisitInfo?.last_visit_date || appointmentForm.followup_of_date) : null,
        category: appointmentForm.category,
        category_ref: appointmentForm.category_ref || null
      };

      const response = await axios.post(`${API}/appointments`, appointmentData);
      const appointment = response.data;
      
      // Send webhook notification (non-blocking - don't await)
      sendWebhook('created', appointment);
      
      // Get doctor details from local state (no API call needed)
      const doctor = doctors.find(d => d.id === appointmentForm.doctor_id);
      
      // Use hospital data from localStorage (already loaded on mount)
      const hospitalData = hospital || JSON.parse(localStorage.getItem('currentHospital') || '{}');
      
      // Get doctor-specific consultation fee (0 if free follow-up visit or Known category)
      let doctorFee = hospitalData.doctor_fees?.[appointmentForm.doctor_id] || hospitalData.consultation_fee || 0;
      if (appointment.is_free_visit || appointmentForm.category === 'Known') {
        doctorFee = 0;  // Free follow-up visit or Known category
      }
      
      // Get baby's short_id for receipt
      const babyRecord = useExistingBaby ? (foundBaby || babies.find(b => b.id === finalBabyId)) : null;
      
      // Prepare receipt data
      const receiptData = {
        appointment_id: appointment.id,
        baby_id: babyRecord?.short_id || finalBabyId?.substring(0, 8) || 'N/A',
        baby_name: useExistingBaby ? (foundBaby?.name || babies.find(b => b.id === finalBabyId)?.name) : appointmentForm.baby_name,
        baby_phone: appointmentForm.baby_phone,
        appointment_contact: appointmentForm.notes, // Fallback contact from notes
        doctor_name: doctor?.name || 'Doctor',
        specialization: doctor?.specialization || '',
        appointment_date: appointmentForm.appointment_date,
        appointment_time: appointmentForm.appointment_time,
        hospital_name: user.hospital_name || hospitalData.name,
        hospital_logo: hospitalData.logo_url || null,
        hospital_address: hospitalData.address || hospitalData.address_line1 || null,
        payment_method: appointmentForm.category === 'Regular' ? appointmentForm.payment_method : `${appointmentForm.category} (Ref: ${appointmentForm.category_ref})`,
        consultation_fee: doctorFee,
        booking_source: 'front_desk',
        category: appointmentForm.category,
        category_ref: appointmentForm.category_ref,
        // Include appointment type and follow-up info for receipt
        appointment_type: useExistingBaby ? appointmentForm.appointment_type : 'new',
        is_free_visit: appointment.is_free_visit || false,
        followup_of_date: appointmentForm.appointment_type === 'followup' ? (lastVisitInfo?.last_visit_date || appointmentForm.followup_of_date) : null
      };
      
      // Update local appointments state immediately (optimistic update)
      // Structure must match what the API returns with nested baby and doctor objects
      const babyData = useExistingBaby 
        ? (foundBaby || babies.find(b => b.id === finalBabyId))
        : {
            id: finalBabyId,
            short_id: finalBabyId?.substring(0, 8).toUpperCase(),
            name: appointmentForm.baby_name,
            date_of_birth: appointmentForm.baby_dob,
            gender: appointmentForm.baby_gender,
            parent_phone: appointmentForm.baby_phone
          };
      
      const newAppointment = {
        ...appointment,
        baby: babyData,
        doctor: {
          id: doctor?.id,
          name: doctor?.name,
          specialization: doctor?.super_specialization || doctor?.specialization
        },
        // Keep flat properties for backwards compatibility
        baby_name: receiptData.baby_name,
        baby_short_id: receiptData.baby_id,
        doctor_name: doctor?.name,
        doctor_specialization: doctor?.super_specialization || doctor?.specialization
      };
      setAppointments(prev => [newAppointment, ...prev]);
      
      // If new baby was created, add to local babies state
      if (!useExistingBaby && finalBabyId) {
        const newBaby = {
          id: finalBabyId,
          short_id: finalBabyId.substring(0, 8).toUpperCase(),
          name: appointmentForm.baby_name,
          date_of_birth: appointmentForm.baby_dob,
          gender: appointmentForm.baby_gender
        };
        setBabies(prev => [newBaby, ...prev]);
      }
      
      setBookingReceipt(receiptData);
      setShowAppointmentDialog(false);
      setShowAppointmentReceipt(true);
      
      setAppointmentForm({
        baby_id: '',
        baby_name: '',
        baby_dob: '',
        baby_gender: 'Male',
        baby_phone: '',
        baby_country_code: '+91',
        doctor_id: '',
        appointment_date: getTodayFormatted(),
        appointment_time: '',
        notes: '',
        payment_method: 'Cash',
        category: 'Regular',
        category_ref: ''
      });
      setFoundBaby(null);
      
      // Show success toast immediately
      toast.success('Appointment booked successfully!');
    } catch (error) {
      console.error('Error creating appointment:', error);
      // Handle Pydantic validation errors
      if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
        const errorMessages = error.response.data.detail.map(err => `${err.loc[1]}: ${err.msg}`).join(', ');
        toast.error(`Validation error: ${errorMessages}`);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create appointment');
      }
    }
  };

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setEditForm({
      status: appointment.status,
      notes: appointment.notes || '',
      appointment_date: appointment.appointment_date,
      appointment_time: appointment.appointment_time
    });
    setShowEditDialog(true);
  };

  const handleUpdateAppointment = async () => {
    try {
      await axios.put(
        `${API}/appointments/${editingAppointment.id}`,
        null,
        { params: editForm }
      );
      
      // Check if date or time changed (rescheduling)
      const isRescheduled = 
        editForm.appointment_date !== editingAppointment.appointment_date ||
        editForm.appointment_time !== editingAppointment.appointment_time;
      
      // Send webhook notification if rescheduled (non-blocking)
      if (isRescheduled) {
        sendWebhook('updated', {
          ...editingAppointment,
          ...editForm,
          is_rescheduled: true
        });
      }
      
      toast.success('Appointment updated successfully!');
      setShowEditDialog(false);
      setEditingAppointment(null);
      loadData();
    } catch (error) {
      console.error('Error updating appointment:', error);
      toast.error('Failed to update appointment');
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    
    try {
      // Get appointment data before deletion for webhook
      const appointmentToDelete = appointments.find(apt => apt.id === appointmentId);
      
      await axios.delete(`${API}/appointments/${appointmentId}`);
      
      // Send webhook notification (non-blocking)
      if (appointmentToDelete) {
        sendWebhook('cancelled', appointmentToDelete);
      }
      
      toast.success('Appointment deleted successfully!');
      loadData();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Failed to delete appointment');
    }
  };

  const handleExtendAvailability = async (doctorId) => {
    const doctor = doctors.find(d => d.id === doctorId);
    if (doctor) {
      setEditingDoctorSchedule(doctor);
      setScheduleForm({ 
        schedule: doctor.schedule || '09:00 AM - 05:00 PM',
        leave_dates: doctor.leave_dates || []
      });
      setShowScheduleDialog(true);
    }
  };

  const handleUpdateDoctorSchedule = async () => {
    try {
      await axios.put(`${API}/doctors/${editingDoctorSchedule.id}`, {
        ...editingDoctorSchedule,
        schedule: scheduleForm.schedule,
        leave_dates: scheduleForm.leave_dates
      });
      
      toast.success('Doctor schedule and leave dates updated successfully!');
      setShowScheduleDialog(false);
      loadData(); // Refresh doctor list
    } catch (error) {
      console.error('Error updating doctor schedule:', error);
      toast.error('Failed to update doctor schedule');
    }
  };

  const handleLeaveDatesUpdate = (newLeaveDates) => {
    setScheduleForm(prev => ({ ...prev, leave_dates: newLeaveDates }));
  };

  // Reset appointment form when dialog closes
  const handleCloseAppointmentDialog = (open) => {
    setShowAppointmentDialog(open);
    if (!open) {
      // Clear form when closing
      setAppointmentForm({
        baby_id: '',
        baby_name: '',
        baby_dob: '',
        baby_gender: 'Male',
        baby_phone: '',
        baby_country_code: '+91',
        doctor_id: '',
        appointment_date: getTodayFormatted(),
        appointment_time: '',
        notes: '',
        payment_method: 'Cash',
        category: 'Regular',
        category_ref: ''
      });
      setFoundBaby(null);
      setUseExistingBaby(true);
    }
  };

  const handleUpdateAvailability = async () => {
    try {
      await axios.put(`${API}/doctors/${extendingDoctor.id}`, {
        name: extendingDoctor.name,
        specialization: extendingDoctor.specialization,
        contact: extendingDoctor.contact,
        photo_url: extendingDoctor.photo_url,
        hospital_id: extendingDoctor.hospital_id,
        schedule: extendForm.schedule
      });
      toast.success('Doctor availability updated!');
      setShowExtendDialog(false);
      setExtendingDoctor(null);
      loadData();
    } catch (error) {
      console.error('Error updating availability:', error);
      toast.error('Failed to update availability');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <h1 className="text-lg md:text-2xl font-bold" style={{fontFamily: 'Space Grotesk'}}>
                Front Desk Dashboard
              </h1>
              <div className="flex items-start space-x-3 md:space-x-4 mt-1">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mt-1">
                  <UserCog className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <div>
                  {hospital && (
                    <p className="text-green-100 text-sm md:text-lg font-bold">{hospital.name}</p>
                  )}
                  <p className="text-green-100 text-xs md:text-base font-medium">{user.name}</p>
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-white hover:bg-white hover:bg-opacity-20 px-2 md:px-4"
            >
              <LogOut className="w-4 h-4 md:w-5 md:h-5 md:mr-2" />
              <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs - Mobile Responsive */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 py-3 sm:py-0">
            <div className="flex space-x-2 sm:space-x-4 md:space-x-8 overflow-x-auto no-scrollbar -mx-4 px-4 sm:mx-0 sm:px-0">
              {[
                { id: 'appointments', label: 'Appointments', icon: Calendar },
                { id: 'doctors', label: 'Doctors', icon: Users },
                { id: 'receipts', label: 'Receipts', icon: FileText },
                { id: 'statistics', label: 'Statistics', icon: BarChart3 }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center space-x-2 py-3 sm:py-4 px-3 sm:px-2 border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'border-green-600 text-green-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium text-xs sm:text-sm md:text-base">{tab.label}</span>
                </button>
              );
            })}
            </div>
            
            {/* Current Date Display */}
            <div className="text-xs sm:text-sm text-gray-600 font-medium py-3 sm:py-4 flex-shrink-0">
              {formatDateWithOrdinal(new Date())}
            </div>
          </div>
        </div>
      </div>

      {/* Content - Mobile Responsive */}
      <div className="container mx-auto px-4 md:px-6 py-6 md:py-8">
        {activeTab === 'appointments' && (
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">Appointment Management</h2>
                {selectedDateFilter && (
                  <p className="text-lg text-gray-600 mt-1">
                    Showing appointments for: <span className="font-semibold text-green-600 text-xl">{formatSelectedDate(selectedDateFilter)}</span>
                  </p>
                )}
              </div>
              
              <div className="flex flex-col gap-2 items-end">
                <div className="flex items-center gap-3">
                  <Button
                    onClick={() => setShowAppointmentDialog(true)}
                    className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Book Appointment
                  </Button>
                </div>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-700 font-medium">Pending {selectedDateFilter && `(${formatSelectedDate(selectedDateFilter)})`}</p>
                    <p className="text-2xl font-bold text-yellow-900">
                      {appointments.filter(apt => 
                        apt.status !== 'completed' && 
                        apt.status !== 'cancelled' && 
                        apt.status !== 'auto_cancelled' &&
                        (!selectedDateFilter || normalizeDateForComparison(apt.appointment_date) === selectedDateFilter) &&
                        (selectedDoctorFilter === 'all' || apt.doctor_id === selectedDoctorFilter)
                      ).length}
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-yellow-600" />
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700 font-medium">Completed {selectedDateFilter && `(${formatSelectedDate(selectedDateFilter)})`}</p>
                    <p className="text-2xl font-bold text-green-900">
                      {appointments.filter(apt => 
                        apt.status === 'completed' && 
                        (!selectedDateFilter || normalizeDateForComparison(apt.appointment_date) === selectedDateFilter) &&
                        (selectedDoctorFilter === 'all' || apt.doctor_id === selectedDoctorFilter)
                      ).length}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </div>

            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Search */}
                <Input
                  placeholder="Search by name or ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full sm:w-64"
                />
                
                {/* Date Filter */}
                <DateInput
                  value={selectedDateFilter}
                  onChange={(e) => setSelectedDateFilter(e.target.value)}
                  className="w-full sm:w-48 h-10"
                />
                
                {/* Doctor Filter */}
                <select
                  value={selectedDoctorFilter}
                  onChange={(e) => setSelectedDoctorFilter(e.target.value)}
                  className="h-10 px-3 rounded-md border border-gray-300 bg-white text-sm w-full sm:w-48"
                >
                  <option value="all">All Doctors</option>
                  {doctors.map(doctor => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name}
                    </option>
                  ))}
                </select>
                
                {/* Clear Filters Button */}
                {(selectedDateFilter !== new Date().toISOString().split('T')[0] || selectedDoctorFilter !== 'all' || searchQuery) && (
                  <Button
                    onClick={() => {
                      setSelectedDateFilter(new Date().toISOString().split('T')[0]); // Reset to today
                      setSelectedDoctorFilter('all');
                      setSearchQuery('');
                    }}
                    variant="outline"
                    className="whitespace-nowrap"
                  >
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading appointments...</p>
                </div>
              </div>
            ) : (
              <>
                {/* Grid Layout for Doctor Appointment Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {(() => {
                    // Get all unique doctor IDs from appointments
                    const doctorIdsWithAppointments = [...new Set(appointments
                      .filter(apt => {
                        // Filter by date
                        if (selectedDateFilter && normalizeDateForComparison(apt.appointment_date) !== selectedDateFilter) return false;
                        // Filter by selected doctor
                        if (selectedDoctorFilter !== 'all' && apt.doctor_id !== selectedDoctorFilter) return false;
                        // Filter by search query
                        if (searchQuery) {
                          const query = searchQuery.toLowerCase();
                          if (!apt.baby?.name?.toLowerCase().includes(query) &&
                              !apt.baby?.id?.toLowerCase().includes(query)) {
                            return false;
                          }
                        }
                        return true;
                      })
                      .map(apt => apt.doctor_id))];
                    
                    // Create doctor objects for each doctor ID (use existing or create placeholder)
                    const doctorsToDisplay = doctorIdsWithAppointments.map(doctorId => {
                      const existingDoctor = doctors.find(d => d.id === doctorId);
                      if (existingDoctor) {
                        return existingDoctor;
                      }
                      // Create placeholder for doctor not in list (e.g., from different hospital via direct booking)
                      const appointment = appointments.find(apt => apt.doctor_id === doctorId);
                      return {
                        id: doctorId,
                        name: appointment?.doctor?.name || 'Unknown',
                        specialization: appointment?.doctor?.super_specialization || appointment?.doctor?.specialization || 'N/A',
                        super_specialization: appointment?.doctor?.super_specialization || null,
                        schedule: appointment?.doctor?.schedule || 'Not available',
                        leave_dates: []
                      };
                    });
                    
                    return doctorsToDisplay.map(doctor => {
                  const doctorAppointments = appointments
                    .filter(apt => {
                      // Filter by doctor
                      if (apt.doctor_id !== doctor.id) return false;
                      
                      // Filter by date
                      if (selectedDateFilter && normalizeDateForComparison(apt.appointment_date) !== selectedDateFilter) return false;
                      
                      // Filter by search query
                      if (searchQuery) {
                        const query = searchQuery.toLowerCase();
                        if (!apt.baby?.name?.toLowerCase().includes(query) &&
                            !apt.baby?.id?.toLowerCase().includes(query)) {
                          return false;
                        }
                      }
                      
                      return true;
                    })
                    .sort((a, b) => {
                      // Quick appointments first (only for pending status)
                      const aIsQuickPending = a.is_quick_appointment && a.status === 'pending';
                      const bIsQuickPending = b.is_quick_appointment && b.status === 'pending';
                      if (aIsQuickPending && !bIsQuickPending) return -1;
                      if (!aIsQuickPending && bIsQuickPending) return 1;
                      
                      // Sort by time (earliest first), then by creation time for same slot
                      const timeA = timeToMinutes(a.appointment_time);
                      const timeB = timeToMinutes(b.appointment_time);
                      if (timeA !== timeB) return timeA - timeB;
                      // For same time slot, sort by created_at (earliest created first)
                      return new Date(a.created_at) - new Date(b.created_at);
                    });

                  // Exclude cancelled and auto_cancelled appointments from total count
                  const activeAppointments = doctorAppointments.filter(a => a.status !== 'cancelled' && a.status !== 'auto_cancelled');
                  const completedCount = activeAppointments.filter(a => a.status === 'completed').length;
                  const totalCount = activeAppointments.length;

                  return (
                    <div key={doctor.id} className="bg-white rounded-xl shadow-md border border-gray-200">
                      {/* Doctor Header - Compact */}
                      <div className="bg-white border-b border-gray-200 p-3 rounded-t-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900">Dr. {doctor.name}</h3>
                            <p className="text-xs text-gray-600">{doctor.super_specialization || doctor.specialization}</p>
                            <p className="text-xs text-gray-600 mt-0.5">
                              ⏰ {doctor.schedule || '09:00 AM - 05:00 PM'}
                            </p>
                            {doctor.leave_dates && doctor.leave_dates.length > 0 && (() => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const futureDates = doctor.leave_dates.filter(date => {
                                const leaveDate = new Date(date);
                                leaveDate.setHours(0, 0, 0, 0);
                                return leaveDate >= today;
                              });
                              return futureDates.length > 0 ? (
                                <div className="text-orange-600 text-xs mt-0.5">
                                  <span className="font-medium">🏖️ On Leave: </span>
                                  <span>{formatLeaveDates(futureDates)}</span>
                                </div>
                              ) : null;
                            })()}
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-green-600">{completedCount}/{totalCount}</p>
                            <p className="text-xs text-gray-600">Cases</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExtendAvailability(doctor.id)}
                            className="ml-2 text-green-600 hover:bg-green-50 border-green-600 h-8 px-2"
                          >
                            <span className="text-xs">Extend</span>
                          </Button>
                        </div>
                      </div>

                      {/* Appointment List - Compact */}
                      <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
                        {doctorAppointments.map((appointment) => {
                          return (
                            <div
                              key={appointment.id}
                              className={`rounded-lg p-3 ${
                                appointment.status === 'completed' 
                                  ? 'bg-green-50 border border-green-200' 
                                  : (appointment.status === 'cancelled' || appointment.status === 'auto_cancelled')
                                  ? 'bg-red-50 border border-red-200'
                                  : appointment.status === 'rescheduled'
                                  ? 'bg-orange-50 border border-orange-200'
                                  : 'bg-white border border-gray-200'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-1 flex-wrap">
                                    <div>
                                      <div className="flex items-center space-x-1.5">
                                        <h4 className="font-bold text-gray-900 text-sm">{appointment.baby?.name || 'Unknown'}</h4>
                                        <span className="font-bold text-gray-900 text-sm truncate">
                                          ({appointment.baby?.short_id || appointment.baby?.id?.substring(0, 8).toUpperCase()})
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-600 mt-0.5">
                                        {appointment.baby ? calculateAge(appointment.baby.date_of_birth) : 'N/A'}
                                        {appointment.baby?.gender && (
                                          <span className="text-gray-500"> • {['male', 'boy', 'm'].includes(appointment.baby.gender.toLowerCase()) ? 'Male' : ['female', 'girl', 'f'].includes(appointment.baby.gender.toLowerCase()) ? 'Female' : appointment.baby.gender}</span>
                                        )}
                                      </p>
                                    </div>
                                    {appointment.status === 'completed' && (
                                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">Completed</span>
                                    )}
                                    {appointment.status === 'rescheduled' && (
                                      <>
                                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">Rescheduled</span>
                                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">Pending</span>
                                      </>
                                    )}
                                    {appointment.status === 'pending' && (
                                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium">Pending</span>
                                    )}
                                    {(appointment.status === 'cancelled' || appointment.status === 'auto_cancelled') && (
                                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                                        {appointment.status === 'auto_cancelled' || appointment.notes?.includes('Auto-cancelled') ? 'Auto Cancelled' : 'Cancelled'}
                                      </span>
                                    )}
                                    {appointment.booking_source === 'direct_booking' && (
                                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">Direct Booking</span>
                                    )}
                                    {/* Quick Appointment Badge */}
                                    {appointment.is_quick_appointment && (
                                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium flex items-center gap-1">
                                        <Zap className="w-3 h-3" />
                                        Quick
                                      </span>
                                    )}
                                    {/* New/Follow-up Badge with Previous Visit Date */}
                                    {appointment.appointment_type === 'followup' ? (
                                      <div className="flex flex-col items-start">
                                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full font-medium">
                                          {appointment.is_free_visit ? 'Follow-Up (Free – 7 Days)' : 'Follow-Up'}
                                        </span>
                                        {appointment.followup_of_date && (
                                          <span className="text-[10px] text-indigo-600 mt-0.5 pl-1">
                                            Prev: {appointment.followup_of_date}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">New</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <p className="text-xs text-gray-600 truncate">
                                      📞 {appointment.baby?.parent_phone || appointment.baby?.mother_contact_phone || appointment.baby?.father_contact_phone || appointment.baby?.emergency_contact_phone || appointment.notes || 'No contact'}
                                    </p>
                                    {appointment.booking_source === 'direct_booking' && (
                                      <div className="flex items-center gap-2 ml-auto bg-gray-50 px-2 py-1 rounded border border-gray-200" onClick={(e) => e.stopPropagation()}>
                                        <span className="text-xs text-gray-600 font-medium">Payment:</span>
                                        <label className="flex items-center gap-1 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded">
                                          <input
                                            type="radio"
                                            name={`payment_${appointment.id}`}
                                            value="Cash"
                                            checked={appointment.payment_method === 'Cash'}
                                            onChange={async (e) => {
                                              e.stopPropagation();
                                              const newValue = e.target.value;
                                              
                                              // Update local state immediately for instant feedback
                                              setAppointments(prevAppointments => 
                                                prevAppointments.map(apt => 
                                                  apt.id === appointment.id 
                                                    ? { ...apt, payment_method: newValue }
                                                    : apt
                                                )
                                              );
                                              
                                              // Then sync with backend
                                              try {
                                                await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/${appointment.id}`, {
                                                  payment_method: newValue
                                                });
                                              } catch (error) {
                                                console.error('Error updating payment method:', error);
                                                // Revert on error - refetch with hospital filter
                                                const userData = localStorage.getItem('healthcareUser');
                                                const currentUser = userData ? JSON.parse(userData) : null;
                                                const hospitalId = currentUser?.hospital_id;
                                                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/appointments-detailed${hospitalId ? `?hospital_id=${hospitalId}` : ''}`);
                                                setAppointments(response.data);
                                              }
                                            }}
                                            className="w-4 h-4 text-green-600 cursor-pointer accent-green-600"
                                          />
                                          <span className="text-xs font-medium text-gray-800">Cash</span>
                                        </label>
                                        <label className="flex items-center gap-1 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded">
                                          <input
                                            type="radio"
                                            name={`payment_${appointment.id}`}
                                            value="Online"
                                            checked={appointment.payment_method === 'Online'}
                                            onChange={async (e) => {
                                              e.stopPropagation();
                                              const newValue = e.target.value;
                                              
                                              // Update local state immediately for instant feedback
                                              setAppointments(prevAppointments => 
                                                prevAppointments.map(apt => 
                                                  apt.id === appointment.id 
                                                    ? { ...apt, payment_method: newValue }
                                                    : apt
                                                )
                                              );
                                              
                                              // Then sync with backend
                                              try {
                                                await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/appointments/${appointment.id}`, {
                                                  payment_method: newValue
                                                });
                                              } catch (error) {
                                                console.error('Error updating payment method:', error);
                                                // Revert on error - refetch with hospital filter
                                                const userData = localStorage.getItem('healthcareUser');
                                                const currentUser = userData ? JSON.parse(userData) : null;
                                                const hospitalId = currentUser?.hospital_id;
                                                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/appointments-detailed${hospitalId ? `?hospital_id=${hospitalId}` : ''}`);
                                                setAppointments(response.data);
                                              }
                                            }}
                                            className="w-4 h-4 text-blue-600 cursor-pointer accent-blue-600"
                                          />
                                          <span className="text-xs font-medium text-gray-800">Online</span>
                                        </label>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2 ml-2">
                                  <p className="text-sm font-semibold text-gray-700 whitespace-nowrap">{appointment.appointment_time}</p>
                                  {appointment.status !== 'completed' && (
                                    <>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleEditAppointment(appointment)}
                                        className="text-blue-600 hover:text-blue-700 h-7 w-7 p-0"
                                      >
                                        <Edit2 className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteAppointment(appointment.id)}
                                        className="text-red-600 hover:text-red-700 h-7 w-7 p-0"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
                </div>

                {/* No appointments message - only show when not loading */}
                {appointments.filter(apt => {
                  // Filter by date
                  if (selectedDateFilter && normalizeDateForComparison(apt.appointment_date) !== selectedDateFilter) return false;
                  // Filter by selected doctor
                  if (selectedDoctorFilter !== 'all' && apt.doctor_id !== selectedDoctorFilter) return false;
                  // Filter by search query
                  if (searchQuery) {
                    const query = searchQuery.toLowerCase();
                    if (!apt.baby?.name?.toLowerCase().includes(query) &&
                        !apt.baby?.id?.toLowerCase().includes(query)) {
                      return false;
                    }
                  }
                  return true;
                }).length === 0 && (
                  <div className="text-center py-12 text-gray-500 bg-white rounded-xl shadow-sm">
                    <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p>No appointments found matching your filters.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'doctors' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Doctor Profiles</h2>
            
            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading doctors...</p>
                </div>
              </div>
            ) : (
              <div className="grid gap-4">
                {doctors.map(doctor => (
                  <div key={doctor.id} className="bg-white rounded-xl shadow-sm border p-6">
                    <div className="flex items-start space-x-4">
                      {doctor.photo_url ? (
                        <img src={`${BACKEND_URL}${doctor.photo_url}`} alt={doctor.name} className="w-16 h-16 rounded-full object-cover" />
                      ) : (
                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                          <Users className="w-8 h-8 text-purple-600" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">Dr. {doctor.name}</h3>
                            <p className="text-purple-600 font-medium mb-2">{doctor.super_specialization || doctor.specialization}</p>
                            {doctor.contact && <p className="text-gray-600 mb-1">📞 {doctor.contact}</p>}
                            {doctor.schedule && (
                              <p className="text-gray-600 text-sm mb-1">⏰ {doctor.schedule}</p>
                            )}
                            {doctor.leave_dates && doctor.leave_dates.length > 0 && (
                              <div className="text-orange-600 text-sm">
                                <p className="font-medium">🏖️ On Leave:</p>
                                <p className="text-xs mt-0.5">{formatLeaveDates(doctor.leave_dates)}</p>
                              </div>
                            )}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExtendAvailability(doctor.id)}
                            className="text-green-600 hover:bg-green-50 border-green-600"
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit Schedule
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {doctors.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p>No doctors available.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'statistics' && (
          <StatisticsTab />
        )}

        {activeTab === 'old_statistics' && (
          <div className="space-y-6">
            {/* Professional Header with White Background */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">Doctor-wise Appointments</h2>
                  <p className="text-gray-600 text-sm">View and manage appointment statistics by doctor</p>
                </div>
                
                {/* Year and Month Selectors */}
                <div className="flex gap-3">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="h-10 px-4 rounded-md border border-gray-300 bg-white text-gray-900 text-sm font-medium hover:border-green-500 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="all">All Months</option>
                    <option value="0">January</option>
                    <option value="1">February</option>
                    <option value="2">March</option>
                    <option value="3">April</option>
                    <option value="4">May</option>
                    <option value="5">June</option>
                    <option value="6">July</option>
                    <option value="7">August</option>
                    <option value="8">September</option>
                    <option value="9">October</option>
                    <option value="10">November</option>
                    <option value="11">December</option>
                  </select>
                  
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="h-10 px-4 rounded-md border border-gray-300 bg-white text-gray-900 text-sm font-medium hover:border-green-500 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    {getAvailableYears().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            
            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading statistics...</p>
                </div>
              </div>
            ) : (
              <>
                {/* Overall Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {(() => {
                    const overallStats = calculateOverallStats();
                    const currentYear = new Date().getFullYear();
                    const currentMonth = new Date().getMonth();
                    const isCurrentYear = selectedYear === currentYear;
                    const isCurrentMonth = selectedMonth === 'all' || parseInt(selectedMonth) === currentMonth;
                    const isAllMonths = selectedMonth === 'all';
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                    const selectedMonthName = isAllMonths ? selectedYear : `${monthNames[parseInt(selectedMonth)]} ${selectedYear}`;
                    
                    return (
                      <>
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                          <p className="text-sm opacity-90">{isCurrentYear && isCurrentMonth ? "Today's Cases" : "Daily (N/A)"}</p>
                          <p className="text-3xl font-bold mt-2">{isCurrentYear && isCurrentMonth ? overallStats.dailyTotal : '-'}</p>
                        </div>
                        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
                          <p className="text-sm opacity-90">
                            {isAllMonths ? (isCurrentYear ? "This Month" : "Monthly (N/A)") : selectedMonthName}
                          </p>
                          <p className="text-3xl font-bold mt-2">
                            {isAllMonths ? (isCurrentYear ? overallStats.monthlyTotal : '-') : overallStats.monthlyTotal}
                          </p>
                        </div>
                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                          <p className="text-sm opacity-90">{isAllMonths ? selectedYear : 'Selected Period'}</p>
                          <p className="text-3xl font-bold mt-2">{overallStats.yearlyTotal}</p>
                        </div>
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white">
                          <p className="text-sm opacity-90">All Time Total</p>
                          <p className="text-3xl font-bold mt-2">{overallStats.allTimeTotal}</p>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Doctor-wise Statistics */}
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Doctor</th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {selectedYear === new Date().getFullYear() && (selectedMonth === 'all' || parseInt(selectedMonth) === new Date().getMonth()) ? 'Today' : 'Daily (N/A)'}
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {selectedMonth === 'all' ? (selectedYear === new Date().getFullYear() ? 'This Month' : 'Monthly (N/A)') : 'Selected Month'}
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {selectedMonth === 'all' ? selectedYear : 'Selected Period'}
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {calculateDoctorStats().map(stat => (
                          <tr key={stat.doctor.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {stat.doctor.photo_url ? (
                                  <img src={`${BACKEND_URL}${stat.doctor.photo_url}`} alt={stat.doctor.name} className="w-10 h-10 rounded-full object-cover mr-3" />
                                ) : (
                                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                                    <Users className="w-5 h-5 text-purple-600" />
                                  </div>
                                )}
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Dr. {stat.doctor.name}</p>
                                  <p className="text-xs text-gray-500">{stat.doctor.super_specialization || stat.doctor.specialization}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-lg font-semibold text-blue-600">
                                {stat.dailyCases > 0 ? stat.dailyCases : (selectedYear === new Date().getFullYear() && (selectedMonth === 'all' || parseInt(selectedMonth) === new Date().getMonth()) ? '0' : '-')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-lg font-semibold text-green-600">
                                {stat.monthlyCases > 0 ? stat.monthlyCases : (selectedMonth === 'all' && selectedYear === new Date().getFullYear() ? '0' : (selectedMonth === 'all' ? '-' : '0'))}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-lg font-semibold text-purple-600">{stat.yearlyCases}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="text-lg font-semibold text-orange-600">{stat.totalCases}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Receipts Tab */}
      {activeTab === 'receipts' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Consultation Receipts</h2>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow-sm border p-4 space-y-4">
            {/* Filter Mode Selection */}
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Filter by:</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setReceiptsFilterMode('date')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    receiptsFilterMode === 'date' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Single Date
                </button>
                <button
                  onClick={() => setReceiptsFilterMode('range')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    receiptsFilterMode === 'range' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Date Range
                </button>
                <button
                  onClick={() => setReceiptsFilterMode('month')}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    receiptsFilterMode === 'month' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Month & Year
                </button>
              </div>
            </div>

            {/* Filter Controls Row */}
            <div className="flex items-center gap-4 flex-wrap">
              {/* Single Date Filter */}
              {receiptsFilterMode === 'date' && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <DateInput
                    value={receiptsSelectedDate}
                    onChange={(e) => setReceiptsSelectedDate(e.target.value)}
                    className="h-10 w-auto"
                  />
                  <button
                    onClick={() => setReceiptsSelectedDate(new Date().toISOString().split('T')[0])}
                    className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors font-medium"
                  >
                    Today
                  </button>
                </div>
              )}

              {/* Date Range Filter */}
              {receiptsFilterMode === 'range' && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-600 font-medium">From:</span>
                    <input
                      type="date"
                      value={receiptsDateFrom}
                      onChange={(e) => setReceiptsDateFrom(e.target.value)}
                      className="h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm text-gray-600 font-medium">To:</span>
                    <input
                      type="date"
                      value={receiptsDateTo}
                      onChange={(e) => setReceiptsDateTo(e.target.value)}
                      className="h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-40"
                    />
                  </div>
                  {(receiptsDateFrom || receiptsDateTo) && (
                    <button
                      onClick={() => { setReceiptsDateFrom(''); setReceiptsDateTo(''); }}
                      className="px-3 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-medium"
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}

              {/* Month & Year Filter */}
              {receiptsFilterMode === 'month' && (
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <select
                    value={receiptsSelectedMonth.split('-')[1] || '01'}
                    onChange={(e) => {
                      const year = receiptsSelectedMonth.split('-')[0] || new Date().getFullYear();
                      setReceiptsSelectedMonth(`${year}-${e.target.value}`);
                    }}
                    className="h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    <option value="01">January</option>
                    <option value="02">February</option>
                    <option value="03">March</option>
                    <option value="04">April</option>
                    <option value="05">May</option>
                    <option value="06">June</option>
                    <option value="07">July</option>
                    <option value="08">August</option>
                    <option value="09">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                  <select
                    value={receiptsSelectedMonth.split('-')[0] || new Date().getFullYear()}
                    onChange={(e) => {
                      const month = receiptsSelectedMonth.split('-')[1] || '01';
                      setReceiptsSelectedMonth(`${e.target.value}-${month}`);
                    }}
                    className="h-10 px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {(() => {
                      // Get unique years from appointments or use current year if no appointments
                      const appointmentYears = appointments
                        .map(apt => {
                          const date = apt.appointment_date;
                          if (date && date.includes('-')) return parseInt(date.split('-')[0]);
                          if (date && date.includes('/')) {
                            const parts = date.split('/');
                            return parseInt(parts[2]); // DD/MM/YYYY format
                          }
                          return null;
                        })
                        .filter(year => year && !isNaN(year));
                      
                      const currentYear = new Date().getFullYear();
                      const uniqueYears = [...new Set([...appointmentYears, currentYear])].sort((a, b) => b - a);
                      
                      return uniqueYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ));
                    })()}
                  </select>
                </div>
              )}

              {/* Doctor Filter */}
              <Select value={receiptsSelectedDoctor} onValueChange={setReceiptsSelectedDoctor}>
                <SelectTrigger className="w-48 h-10">
                  <SelectValue placeholder="All Doctors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doctors</SelectItem>
                  {doctors.map((doctor) => (
                    <SelectItem key={doctor.id} value={doctor.id}>
                      Dr. {doctor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Search - searches across ALL dates */}
              <div className="flex-1 min-w-64">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by ID, Name or Contact (searches all dates)"
                    value={receiptsSearchQuery}
                    onChange={(e) => setReceiptsSearchQuery(e.target.value)}
                    className="pl-10 h-10 w-full"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Receipts List */}
          {(() => {
            // Filter appointments based on search or date/range/month
            const filteredAppointments = appointments.filter(apt => {
              const baby = babies.find(b => b.id === apt.baby_id);
              
              // Apply doctor filter first
              if (receiptsSelectedDoctor !== 'all' && apt.doctor_id !== receiptsSelectedDoctor) {
                return false;
              }
              
              // If searching, ignore date filter and search across ALL dates
              if (receiptsSearchQuery) {
                const searchLower = receiptsSearchQuery.toLowerCase();
                
                // Search in baby short ID (8 letters)
                const shortIdMatch = baby?.short_id?.toLowerCase().includes(searchLower);
                
                // Search in baby name
                const nameMatch = baby?.name?.toLowerCase().includes(searchLower);
                
                // Search in contact numbers
                const phoneMatch = baby?.parent_phone?.includes(receiptsSearchQuery) || 
                                   baby?.mother_contact_phone?.includes(receiptsSearchQuery) ||
                                   baby?.father_contact_phone?.includes(receiptsSearchQuery) ||
                                   baby?.emergency_contact_phone?.includes(receiptsSearchQuery) ||
                                   apt.notes?.includes(receiptsSearchQuery);
                
                return shortIdMatch || nameMatch || phoneMatch;
              } else {
                // Apply date filter based on selected mode
                const aptDate = normalizeDateForComparison(apt.appointment_date);
                
                if (receiptsFilterMode === 'date') {
                  // Single date filter
                  return aptDate === receiptsSelectedDate;
                } else if (receiptsFilterMode === 'range') {
                  // Date range filter - require at least one date to be set
                  if (!receiptsDateFrom && !receiptsDateTo) return false; // Show nothing if no range set
                  if (receiptsDateFrom && !receiptsDateTo) return aptDate >= receiptsDateFrom;
                  if (!receiptsDateFrom && receiptsDateTo) return aptDate <= receiptsDateTo;
                  return aptDate >= receiptsDateFrom && aptDate <= receiptsDateTo;
                } else if (receiptsFilterMode === 'month') {
                  // Month & Year filter (YYYY-MM format)
                  const aptMonth = aptDate.substring(0, 7); // Get YYYY-MM from YYYY-MM-DD
                  return aptMonth === receiptsSelectedMonth;
                }
                return true;
              }
            });

            // Sort by date (newest first), then by time (earliest first)
            const sortedAppointments = [...filteredAppointments].sort((a, b) => {
              // Normalize dates for proper comparison
              const dateA = normalizeDateForComparison(a.appointment_date);
              const dateB = normalizeDateForComparison(b.appointment_date);
              const dateCompare = dateB.localeCompare(dateA); // Newest date first
              if (dateCompare !== 0) return dateCompare;
              // Sort by time (earliest first)
              return timeToMinutes(a.appointment_time) - timeToMinutes(b.appointment_time);
            });

            if (sortedAppointments.length === 0) {
              // Determine appropriate message based on filter mode
              let noResultsMessage = '';
              if (receiptsSearchQuery) {
                noResultsMessage = 'No receipts found matching your search';
              } else if (receiptsFilterMode === 'date') {
                noResultsMessage = `No receipts found for ${formatDateToDDMMYYYY(receiptsSelectedDate)}`;
              } else if (receiptsFilterMode === 'range') {
                if (!receiptsDateFrom && !receiptsDateTo) {
                  noResultsMessage = 'Please select a date range to view receipts';
                } else {
                  noResultsMessage = `No receipts found for the selected date range`;
                }
              } else if (receiptsFilterMode === 'month') {
                noResultsMessage = `No receipts found for the selected month`;
              }
              
              return (
                <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">{noResultsMessage}</p>
                </div>
              );
            }

            // Calculate statistics for ALL appointments (including direct bookings with payment method set)
            // Exclude cancelled, auto_cancelled, Known categories and free follow-ups from fee calculations
            const isCancelled = (apt) => apt.status === 'cancelled' || apt.status === 'auto_cancelled';
            
            // Check if appointment is free (free visit or known) - use truthy check to match table logic
            const isFreeAppointment = (apt) => {
              return !!apt.is_free_visit || apt.category === 'Known';
            };
            
            // Get consultation fee for an appointment
            const getConsultationFee = (apt) => {
              if (isCancelled(apt) || isFreeAppointment(apt)) return 0;
              return hospital?.doctor_fees?.[apt.doctor_id] || hospital?.consultation_fee || 0;
            };
            
            // Filter paid appointments (non-cancelled, non-free, WITH actual fee > 0)
            const paidAppointments = sortedAppointments.filter(apt => 
              !isCancelled(apt) && !isFreeAppointment(apt) && getConsultationFee(apt) > 0
            );
            
            const totalCash = paidAppointments
              .filter(apt => (apt.payment_method || 'Cash') === 'Cash')
              .reduce((sum, apt) => sum + getConsultationFee(apt), 0);
            const totalOnline = paidAppointments
              .filter(apt => apt.payment_method === 'Online')
              .reduce((sum, apt) => sum + getConsultationFee(apt), 0);
            const grandTotal = totalCash + totalOnline;
            const totalAppointments = paidAppointments.length;
            const freeAppointments = sortedAppointments.filter(apt => !isCancelled(apt) && isFreeAppointment(apt)).length;
            const cancelledAppointments = sortedAppointments.filter(apt => isCancelled(apt)).length;
            
            // Count statistics
            const cashCount = paidAppointments.filter(apt => (apt.payment_method || 'Cash') === 'Cash').length;
            const onlineCount = paidAppointments.filter(apt => apt.payment_method === 'Online').length;

            return (
              <>
                {/* Statistics Cards - Amount */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">Total Cash</p>
                        <p className="text-2xl font-bold text-green-700">₹{totalCash}</p>
                      </div>
                      <div className="bg-green-100 p-3 rounded-full">
                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-xs text-green-600 mt-2">
                      {cashCount} paid appointment(s)
                    </p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">Total Online</p>
                        <p className="text-2xl font-bold text-blue-700">₹{totalOnline}</p>
                      </div>
                      <div className="bg-blue-100 p-3 rounded-full">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      {onlineCount} paid appointment(s)
                    </p>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">Grand Total</p>
                        <p className="text-2xl font-bold text-purple-700">₹{grandTotal}</p>
                      </div>
                      <div className="bg-purple-100 p-3 rounded-full">
                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-xs text-purple-600 mt-2">
                      {totalAppointments} paid • {freeAppointments} free{cancelledAppointments > 0 ? ` • ${cancelledAppointments} cancelled` : ''}
                    </p>
                  </div>
                </div>

                {/* Receipts Table */}
                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-9 gap-2 px-4 py-3 bg-gray-50 border-b text-sm font-semibold text-gray-700">
                    <div>Date</div>
                    <div>ID</div>
                    <div>Name</div>
                    <div>Contact</div>
                    <div>Doctor</div>
                    <div>Visit Type</div>
                    <div>Category</div>
                    <div>Payment</div>
                    <div className="text-right">Fee</div>
                  </div>

                  {/* Table Rows */}
                  <div className="divide-y">
                    {sortedAppointments.map(apt => {
                      const baby = babies.find(b => b.id === apt.baby_id);
                      const doctor = doctors.find(d => d.id === apt.doctor_id);
                      const contact = apt.notes || baby?.parent_phone || baby?.mother_contact_phone || baby?.father_contact_phone || baby?.emergency_contact_phone || 'N/A';
                      const doctorDisplay = doctor ? `Dr. ${doctor.name}` : 'N/A';
                      
                      // Check if appointment is cancelled
                      const isCancelledApt = apt.status === 'cancelled' || apt.status === 'auto_cancelled';
                      
                      // Calculate fee based on category, free visit status, and cancelled status
                      let doctorFee = hospital?.doctor_fees?.[apt.doctor_id] || hospital?.consultation_fee || 0;
                      if (isCancelledApt || apt.is_free_visit || apt.category === 'Known') {
                        doctorFee = 0;
                      }
                      
                      // Determine visit type display
                      let visitTypeDisplay = 'New';
                      let visitTypeBgClass = 'bg-blue-100 text-blue-700';
                      if (isCancelledApt) {
                        visitTypeDisplay = apt.status === 'auto_cancelled' ? 'Auto Cancelled' : 'Cancelled';
                        visitTypeBgClass = 'bg-red-100 text-red-700';
                      } else if (apt.appointment_type === 'followup') {
                        if (apt.is_free_visit) {
                          visitTypeDisplay = 'Follow-Up (Free)';
                          visitTypeBgClass = 'bg-green-100 text-green-700';
                        } else {
                          visitTypeDisplay = 'Follow-Up';
                          visitTypeBgClass = 'bg-indigo-100 text-indigo-700';
                        }
                      }
                      
                      // Determine category display
                      const categoryDisplay = apt.category || 'Regular';
                      let categoryBgClass = 'bg-gray-100 text-gray-700';
                      if (apt.category === 'Known') {
                        categoryBgClass = 'bg-purple-100 text-purple-700';
                      }
                      
                      // Payment display - show N/A for cancelled appointments
                      const paymentDisplay = isCancelledApt 
                        ? 'N/A'
                        : (apt.category === 'Known' 
                          ? 'N/A' 
                          : (apt.payment_method || 'Cash'));
                      
                      return (
                        <div 
                          key={apt.id} 
                          className={`grid grid-cols-9 gap-2 px-4 py-3 hover:bg-gray-50 cursor-pointer text-sm ${isCancelledApt ? 'bg-red-50 opacity-75' : ''}`}
                          onClick={() => {
                            setBookingReceipt({
                              baby_id: baby?.short_id || apt.baby_id?.substring(0, 8),
                              baby_name: baby?.name || 'N/A',
                              baby_phone: contact,
                              appointment_contact: apt.notes,
                              doctor_name: doctor?.name || 'N/A',
                              specialization: doctor?.specialization || '',
                              appointment_date: apt.appointment_date,
                              appointment_time: apt.appointment_time,
                              hospital_name: user?.hospital_name || hospital?.name || 'N/A',
                              hospital_logo: hospital?.logo_url || null,
                              hospital_address: hospital?.address || hospital?.address_line1 || null,
                              payment_method: paymentDisplay,
                              consultation_fee: doctorFee,
                              booking_source: apt.booking_source,
                              appointment_type: apt.appointment_type,
                              is_free_visit: apt.is_free_visit,
                              is_cancelled: isCancelledApt,
                              status: apt.status,
                              category: apt.category,
                              category_ref: apt.category_ref,
                              followup_of_date: apt.followup_of_date
                            });
                            setShowAppointmentReceipt(true);
                          }}
                        >
                          <div className="text-gray-900">
                            {formatDateToDDMMYYYY(apt.appointment_date)}
                          </div>
                          <div className="font-medium text-blue-600 uppercase">
                            {baby?.short_id || apt.baby_id?.substring(0, 8)}
                          </div>
                          <div className="text-gray-900 truncate">
                            {baby?.name || 'N/A'}
                          </div>
                          <div className="text-gray-600 truncate">
                            {contact}
                          </div>
                          <div className="text-gray-900 truncate">
                            {doctorDisplay}
                          </div>
                          <div>
                            <span className={`px-2 py-1 rounded text-sm font-medium ${visitTypeBgClass}`}>
                              {visitTypeDisplay}
                            </span>
                          </div>
                          <div>
                            <span className={`px-2 py-1 rounded text-sm font-medium ${categoryBgClass}`}>
                              {categoryDisplay}
                            </span>
                          </div>
                          <div className="text-gray-700">
                            {paymentDisplay}
                          </div>
                          <div className={`text-right font-medium ${doctorFee === 0 ? 'text-green-600' : 'text-gray-900'}`}>
                            ₹{doctorFee}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Book Appointment Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={handleCloseAppointmentDialog}>
        <DialogContent className="sm:max-w-md z-50 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book Appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  checked={useExistingBaby}
                  onChange={() => setUseExistingBaby(true)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Existing</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!useExistingBaby}
                  onChange={() => setUseExistingBaby(false)}
                  className="w-4 h-4"
                />
                <span className="text-sm">New</span>
              </label>
            </div>

            {useExistingBaby ? (
              <>
                <div>
                  <Label htmlFor="baby_id">ID</Label>
                  <Input
                    id="baby_id"
                    value={appointmentForm.baby_id}
                    onChange={(e) => {
                      setAppointmentForm({...appointmentForm, baby_id: e.target.value.toUpperCase()});
                      setFoundBaby(null);
                    }}
                    placeholder="Enter ID"
                  />
                </div>
                
                <div className="text-center text-gray-500 text-sm">OR</div>
                
                <div>
                  <Label htmlFor="search_baby_name">Name</Label>
                  <Input
                    id="search_baby_name"
                    value={appointmentForm.baby_name}
                    onChange={(e) => {
                      const value = e.target.value;
                      const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
                      setAppointmentForm({...appointmentForm, baby_name: capitalized});
                      setFoundBaby(null);
                    }}
                    placeholder="Enter Name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="search_baby_dob">Date of Birth (DD/MM/YYYY)</Label>
                  <Input
                    id="search_baby_dob"
                    value={appointmentForm.baby_dob}
                    onChange={(e) => handleDobInput(e.target.value, false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' || e.key === 'Delete') {
                        const value = appointmentForm.baby_dob;
                        if (value.endsWith('/')) {
                          e.preventDefault();
                          setAppointmentForm({...appointmentForm, baby_dob: value.slice(0, -1)});
                        }
                      }
                    }}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                  />
                </div>
                
                <Button
                  type="button"
                  onClick={handleSearchBaby}
                  variant="outline"
                  className="w-full"
                >
                  Search
                </Button>
                
                {foundBaby && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-green-700">✓ Found</p>
                    <p className="text-sm text-green-800">{foundBaby.name}</p>
                    <p className="text-xs text-green-600">ID: <span className="uppercase font-mono">{foundBaby.short_id || foundBaby.id.substring(0, 8).toUpperCase()}</span></p>
                    <p className="text-xs text-green-600">DOB: {foundBaby.date_of_birth}</p>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="existing_baby_phone">Phone Number for Appointment *</Label>
                  <PhoneInput
                    value={appointmentForm.baby_phone}
                    onChange={(value) => setAppointmentForm({...appointmentForm, baby_phone: value})}
                    countryCode={appointmentForm.baby_country_code}
                    onCountryCodeChange={(code) => setAppointmentForm({...appointmentForm, baby_country_code: code})}
                    placeholder="Enter 10-digit mobile number"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Phone number is for appointment only</p>
                </div>
                
                {/* Appointment Type Selection - Only for existing patients */}
                {foundBaby && (
                  <div>
                    <Label>Appointment Type *</Label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="appointment_type"
                          value="new"
                          checked={appointmentForm.appointment_type === 'new'}
                          onChange={(e) => setAppointmentForm({...appointmentForm, appointment_type: e.target.value, followup_of_date: ''})}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium text-gray-700">New Visit</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="appointment_type"
                          value="followup"
                          checked={appointmentForm.appointment_type === 'followup'}
                          onChange={(e) => setAppointmentForm({...appointmentForm, appointment_type: e.target.value})}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm font-medium text-gray-700">Follow-up</span>
                      </label>
                    </div>
                    {/* Show free visit info if within 7 days */}
                    {appointmentForm.appointment_type === 'followup' && lastVisitInfo?.is_within_7_days && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-700 font-medium">
                          ✅ Free Follow-up Visit (within 7 days of last visit on {lastVisitInfo.last_visit_date})
                        </p>
                        <p className="text-xs text-green-600">No consultation fee will be charged</p>
                      </div>
                    )}
                    {appointmentForm.appointment_type === 'followup' && lastVisitInfo && !lastVisitInfo.is_within_7_days && lastVisitInfo.has_previous_visit && (
                      <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-xs text-yellow-700">
                          Last visit was {lastVisitInfo.days_since_last_visit} days ago on {lastVisitInfo.last_visit_date}
                        </p>
                        <p className="text-xs text-yellow-600">Normal consultation fee applies</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <Label htmlFor="baby_name">Name *</Label>
                  <Input
                    id="baby_name"
                    value={appointmentForm.baby_name}
                    onChange={(e) => {
                      const value = e.target.value;
                      const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
                      setAppointmentForm({...appointmentForm, baby_name: capitalized});
                    }}
                    placeholder="e.g., Aarav"
                  />
                </div>
                <div>
                  <Label htmlFor="baby_dob">Date of Birth (DD/MM/YYYY) *</Label>
                  <Input
                    id="baby_dob"
                    value={appointmentForm.baby_dob}
                    onChange={(e) => handleDobInput(e.target.value, true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' || e.key === 'Delete') {
                        const value = appointmentForm.baby_dob;
                        if (value.endsWith('/')) {
                          e.preventDefault();
                          setAppointmentForm({...appointmentForm, baby_dob: value.slice(0, -1)});
                        }
                      }
                    }}
                    placeholder="DD/MM/YYYY"
                    maxLength={10}
                  />
                </div>
                <div>
                  <Label htmlFor="baby_gender">Gender *</Label>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    <button
                      type="button"
                      onClick={() => setAppointmentForm({...appointmentForm, baby_gender: 'Male'})}
                      className={`h-12 rounded-lg border-2 font-semibold transition-all ${
                        appointmentForm.baby_gender === 'Male'
                          ? 'bg-blue-500 text-white border-blue-600 shadow-lg'
                          : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      👦 Male
                    </button>
                    <button
                      type="button"
                      onClick={() => setAppointmentForm({...appointmentForm, baby_gender: 'Female'})}
                      className={`h-12 rounded-lg border-2 font-semibold transition-all ${
                        appointmentForm.baby_gender === 'Female'
                          ? 'bg-pink-500 text-white border-pink-600 shadow-lg'
                          : 'bg-white text-pink-600 border-pink-300 hover:bg-pink-50'
                      }`}
                    >
                      👧 Female
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="baby_phone">Phone Number for Appointment *</Label>
                  <PhoneInput
                    value={appointmentForm.baby_phone}
                    onChange={(value) => setAppointmentForm({...appointmentForm, baby_phone: value})}
                    countryCode={appointmentForm.baby_country_code}
                    onCountryCodeChange={(code) => setAppointmentForm({...appointmentForm, baby_country_code: code})}
                    placeholder="Enter 10-digit mobile number"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Phone number is for appointment only, not saved to baby profile</p>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="doctor_id">Select Doctor *</Label>
              <select
                id="doctor_id"
                value={appointmentForm.doctor_id}
                onChange={(e) => {
                  const newDoctorId = e.target.value;
                  setAppointmentForm({...appointmentForm, doctor_id: newDoctorId});
                  // Fetch last visit info when doctor is selected and baby is found
                  if (foundBaby && newDoctorId) {
                    fetchLastVisitInfo(foundBaby.id, newDoctorId);
                  }
                }}
                className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
              >
                <option value="">Select Doctor</option>
                {doctors
                  .filter(doctor => {
                    // Filter out doctors who are on leave for the selected date
                    if (!appointmentForm.appointment_date) return true;
                    if (!doctor.leave_dates || doctor.leave_dates.length === 0) return true;
                    
                    // Normalize both dates to YYYY-MM-DD format for comparison
                    const selectedDate = appointmentForm.appointment_date; // Already in YYYY-MM-DD from date input
                    
                    // Check if doctor is on leave on the selected date
                    const isOnLeave = doctor.leave_dates.some(leaveDate => {
                      // Normalize leave date (might be in different formats)
                      const normalizedLeaveDate = normalizeDateForComparison(leaveDate);
                      return normalizedLeaveDate === selectedDate;
                    });
                    
                    return !isOnLeave; // Only show if NOT on leave
                  })
                  .map(doctor => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name} - {doctor.super_specialization || doctor.specialization}
                    </option>
                  ))
                }
              </select>
              {appointmentForm.appointment_date && doctors.filter(d => {
                if (!d.leave_dates) return false;
                const selectedDate = appointmentForm.appointment_date;
                return d.leave_dates.some(leaveDate => 
                  normalizeDateForComparison(leaveDate) === selectedDate
                );
              }).length > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  ⚠️ Some doctors are on leave on this date
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="appointment_date">Appointment Date *</Label>
              <div className="relative">
                <DateInput
                  id="appointment_date"
                  value={appointmentForm.appointment_date}
                  onChange={(e) => {
                    setAppointmentForm({...appointmentForm, appointment_date: e.target.value});
                    // Force blur to close calendar after selection
                    e.target.blur();
                  }}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm opacity-0 absolute inset-0 z-10 cursor-pointer"
                />
                {/* Custom date display for consistent DD/MM/YYYY format */}
                <div className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm flex items-center justify-between cursor-pointer">
                  <span className={appointmentForm.appointment_date ? 'text-gray-900' : 'text-gray-400'}>
                    {appointmentForm.appointment_date ? formatDateToDDMMYYYY(appointmentForm.appointment_date) : 'Select date'}
                  </span>
                  <Calendar className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="appointment_time">Appointment Time *</Label>
              <select
                id="appointment_time"
                value={appointmentForm.appointment_time}
                onChange={(e) => setAppointmentForm({...appointmentForm, appointment_time: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                disabled={!appointmentForm.doctor_id || !appointmentForm.appointment_date}
              >
                <option value="">
                  {!appointmentForm.doctor_id ? 'Select doctor first' : 
                   !appointmentForm.appointment_date ? 'Select date first' : 
                   'Select Time'}
                </option>
                {getAvailableTimeSlots(appointmentForm.doctor_id, appointmentForm.appointment_date).map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
              {appointmentForm.doctor_id && appointmentForm.appointment_date && 
               getAvailableTimeSlots(appointmentForm.doctor_id, appointmentForm.appointment_date).length === 0 && (() => {
                 const selectedDoctor = doctors.find(d => d.id === appointmentForm.doctor_id);
                 const isOnLeave = selectedDoctor?.leave_dates?.includes(appointmentForm.appointment_date);
                 return (
                   <p className="text-xs text-red-500 mt-1 font-medium">
                     {isOnLeave ? '⚠️ Doctor is on leave on this date' : 'Doctor not available on selected date'}
                   </p>
                 );
               })()}
            </div>

            {/* Category Selection */}
            <div>
              <Label>Category *</Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="category"
                    value="Regular"
                    checked={appointmentForm.category === 'Regular'}
                    onChange={(e) => setAppointmentForm({...appointmentForm, category: e.target.value, category_ref: ''})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Regular</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="category"
                    value="Known"
                    checked={appointmentForm.category === 'Known'}
                    onChange={(e) => setAppointmentForm({...appointmentForm, category: e.target.value})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Known</span>
                </label>
              </div>
            </div>

            {/* Ref field - shown only for Known */}
            {appointmentForm.category === 'Known' && (
              <div>
                <Label htmlFor="category_ref">Ref (Reference) *</Label>
                <Input
                  id="category_ref"
                  value={appointmentForm.category_ref}
                  onChange={(e) => setAppointmentForm({...appointmentForm, category_ref: e.target.value})}
                  placeholder="Enter reference/clarification..."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Consultation fee: ₹0 (No charge)</p>
              </div>
            )}

            {/* Payment Method - shown only for Regular category */}
            {appointmentForm.category === 'Regular' && (
              <div>
                <Label>Payment Method *</Label>
                <div className="flex gap-6 mt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="payment_method"
                      value="Cash"
                      checked={appointmentForm.payment_method === 'Cash'}
                      onChange={(e) => setAppointmentForm({...appointmentForm, payment_method: e.target.value})}
                      className="w-4 h-4 text-blue-600"
                      required
                    />
                    <span className="text-sm font-medium text-gray-700">Cash</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="payment_method"
                      value="Online"
                      checked={appointmentForm.payment_method === 'Online'}
                      onChange={(e) => setAppointmentForm({...appointmentForm, payment_method: e.target.value})}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Online</span>
                  </label>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={appointmentForm.notes}
                onChange={(e) => setAppointmentForm({...appointmentForm, notes: e.target.value})}
                placeholder="Additional notes..."
                rows={3}
              />
            </div>

            <div className="flex space-x-2">
              <Button onClick={handleCreateAppointment} className="flex-1">
                Book Appointment
              </Button>
              <Button variant="outline" onClick={() => setShowAppointmentDialog(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md z-50">
          <DialogHeader>
            <DialogTitle>Edit Appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit_status">Status</Label>
              <select
                id="edit_status"
                value={editForm.status}
                onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
              >
                <option value="scheduled">Scheduled</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="rescheduled">Rescheduled</option>
              </select>
            </div>

            <div>
              <Label htmlFor="edit_date">Date</Label>
              <DateInput
                id="edit_date"
                value={editForm.appointment_date}
                onChange={(e) => {
                  setEditForm({...editForm, appointment_date: e.target.value});
                  // Force blur to close calendar after selection
                  e.target.blur();
                }}
                className="w-full"
              />
            </div>

            <div>
              <Label htmlFor="edit_time">Time</Label>
              <select
                id="edit_time"
                value={editForm.appointment_time}
                onChange={(e) => setEditForm({...editForm, appointment_time: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                disabled={!editingAppointment?.doctor_id || !editForm.appointment_date}
              >
                <option value="">Select Time</option>
                {editingAppointment && editForm.appointment_date && 
                 getAvailableTimeSlots(editingAppointment.doctor_id, editForm.appointment_date)
                   .concat(editForm.appointment_time) // Include current time even if booked
                   .filter((v, i, a) => a.indexOf(v) === i) // Remove duplicates
                   .sort((a, b) => timeToMinutes(a) - timeToMinutes(b))
                   .map(time => (
                     <option key={time} value={time}>{time}</option>
                   ))
                }
              </select>
            </div>

            <div>
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                rows={3}
              />
            </div>

            <div className="flex space-x-2">
              <Button onClick={handleUpdateAppointment} className="flex-1">
                Update
              </Button>
              <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Extend Availability Dialog */}
      <Dialog open={showExtendDialog} onOpenChange={setShowExtendDialog}>
        <DialogContent className="sm:max-w-md z-50">
          <DialogHeader>
            <DialogTitle>Extend Doctor Availability</DialogTitle>
          </DialogHeader>
          {extendingDoctor && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-2">
                  {extendingDoctor.name} - {extendingDoctor.super_specialization || extendingDoctor.specialization}
                </p>
              </div>

              <div>
                <Label htmlFor="schedule">Availability Schedule</Label>
                <Input
                  id="schedule"
                  value={extendForm.schedule}
                  onChange={(e) => setExtendForm({...extendForm, schedule: e.target.value})}
                  placeholder="e.g., 09:00 AM - 08:00 PM"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the extended working hours (e.g., "09:00 AM - 08:00 PM")
                </p>
              </div>

              <div className="flex space-x-2">
                <Button onClick={handleUpdateAvailability} className="flex-1">
                  Update Availability
                </Button>
                <Button variant="outline" onClick={() => setShowExtendDialog(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Doctor Schedule Edit Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Doctor Schedule & Leave</DialogTitle>
            <DialogDescription>
              Update working hours and leave dates for Dr. {editingDoctorSchedule?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Working Hours */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Working Hours</h3>
                <div className="space-y-2">
                  <Label htmlFor="doctor_schedule">Schedule *</Label>
                  <Input
                    id="doctor_schedule"
                    value={scheduleForm.schedule}
                    onChange={(e) => setScheduleForm(prev => ({ ...prev, schedule: e.target.value }))}
                    placeholder="e.g., 09:00 AM - 05:00 PM"
                  />
                  <p className="text-xs text-gray-500">Format: HH:MM AM/PM - HH:MM AM/PM</p>
                </div>
              </div>

              {/* Leave Management */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Leave Dates</h3>
                <DoctorLeaveManagement
                  leaveDates={scheduleForm.leave_dates}
                  onUpdate={handleLeaveDatesUpdate}
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateDoctorSchedule} className="bg-green-600 hover:bg-green-700">
                Update Schedule & Leave
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Appointment Receipt Dialog */}
      <Dialog open={showAppointmentReceipt} onOpenChange={setShowAppointmentReceipt}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader className="no-print">
            <DialogTitle className="text-center text-lg">Booking Confirmed!</DialogTitle>
          </DialogHeader>
          
          {bookingReceipt && (
            <>
              {/* Print Styles - Global approach */}
              <style>{`
                @media print {
                  /* Hide everything on the page */
                  body > * {
                    display: none !important;
                  }
                  
                  /* Show the receipt container */
                  body > .receipt-print-container {
                    display: block !important;
                  }
                }
                
                .receipt-print-container {
                  display: none;
                }
                
                @media print {
                  .receipt-print-container {
                    display: block !important;
                    position: fixed !important;
                    top: 0 !important;
                    left: 0 !important;
                    width: 100% !important;
                    height: 100% !important;
                    background: white !important;
                    z-index: 99999 !important;
                    padding: 20mm !important;
                  }
                }
              `}</style>

              {/* Success Message - Hidden in Print */}
              <div className="text-center py-3 bg-green-50 rounded-lg mb-4 no-print">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Appointment successfully booked</p>
              </div>

              {/* Receipt Content - Screen Version */}
              <div className="border border-gray-200 rounded-lg p-4">
                {/* Hospital Branding */}
                <div style={{ textAlign: 'center', paddingBottom: '12px', borderBottom: '1px dashed #d1d5db', marginBottom: '12px' }}>
                  {bookingReceipt.hospital_logo && (
                    <img 
                      src={bookingReceipt.hospital_logo.startsWith('http') ? bookingReceipt.hospital_logo : `${BACKEND_URL}${bookingReceipt.hospital_logo}`}
                      alt="Hospital logo" 
                      style={{ height: '48px', width: '48px', objectFit: 'contain', margin: '0 auto 8px auto', display: 'block' }}
                      crossOrigin="anonymous"
                    />
                  )}
                  <h2 style={{ fontSize: '16px', fontWeight: 'bold', margin: '0' }}>{bookingReceipt.hospital_name}</h2>
                  {bookingReceipt.hospital_address && (
                    <p style={{ fontSize: '11px', color: '#4b5563', margin: '4px 0 0 0' }}>{bookingReceipt.hospital_address}</p>
                  )}
                  <p style={{ fontSize: '14px', color: '#374151', marginTop: '8px', fontWeight: '600' }}>Appointment Receipt</p>
                </div>

                {/* Receipt Details */}
                <div style={{ fontSize: '13px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>ID:</span>
                    <span style={{ fontWeight: '500' }}>{bookingReceipt.baby_id?.toUpperCase()}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Name:</span>
                    <span style={{ fontWeight: '500' }}>{bookingReceipt.baby_name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Contact:</span>
                    <span style={{ fontWeight: '500' }}>{bookingReceipt.baby_phone || bookingReceipt.appointment_contact || 'N/A'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Doctor:</span>
                    <span style={{ fontWeight: '500' }}>{bookingReceipt.doctor_name}</span>
                  </div>
                  {bookingReceipt.specialization && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span style={{ color: '#4b5563' }}>Specialization:</span>
                      <span style={{ fontWeight: '500' }}>{bookingReceipt.specialization}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Date:</span>
                    <span style={{ fontWeight: '500' }}>{formatDateToDDMMYYYY(bookingReceipt.appointment_date)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Time:</span>
                    <span style={{ fontWeight: '500' }}>{bookingReceipt.appointment_time}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Visit Type:</span>
                    <span style={{ fontWeight: '500', color: bookingReceipt.is_free_visit ? '#16a34a' : 'inherit' }}>
                      {bookingReceipt.appointment_type === 'followup' 
                        ? (bookingReceipt.is_free_visit ? 'Follow-Up (Free)' : 'Follow-Up')
                        : 'New'}
                    </span>
                  </div>
                  {bookingReceipt.appointment_type === 'followup' && bookingReceipt.followup_of_date && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span style={{ color: '#4b5563' }}>Previous Visit:</span>
                      <span style={{ fontWeight: '500' }}>{bookingReceipt.followup_of_date}</span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span style={{ color: '#4b5563' }}>Category:</span>
                    <span style={{ fontWeight: '500', color: bookingReceipt.category === 'Known' ? '#d97706' : 'inherit' }}>
                      {bookingReceipt.category || 'Regular'}
                    </span>
                  </div>
                  {bookingReceipt.category === 'Known' && bookingReceipt.category_ref && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span style={{ color: '#4b5563' }}>Reference:</span>
                      <span style={{ fontWeight: '500' }}>{bookingReceipt.category_ref}</span>
                    </div>
                  )}
                  {bookingReceipt.category !== 'Known' && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                      <span style={{ color: '#4b5563' }}>Payment:</span>
                      <span style={{ fontWeight: '500' }}>{bookingReceipt.payment_method || 'Cash'}</span>
                    </div>
                  )}
                </div>
                
                {/* Fee Section */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '15px', fontWeight: 'bold', paddingTop: '12px', marginTop: '8px', borderTop: '1px solid #d1d5db' }}>
                  <span style={{ color: '#111827' }}>Consultation Fee:</span>
                  <span style={{ color: bookingReceipt.consultation_fee === 0 ? '#16a34a' : '#111827' }}>
                    {bookingReceipt.consultation_fee === 0 
                      ? '₹0 (Free)' 
                      : (bookingReceipt.consultation_fee > 0 ? `₹${bookingReceipt.consultation_fee}` : 'To be confirmed')}
                  </span>
                </div>

                {/* Footer */}
                <div style={{ textAlign: 'center', fontSize: '11px', color: '#6b7280', fontStyle: 'italic', marginTop: '16px', paddingTop: '12px', borderTop: '1px dashed #d1d5db' }}>
                  <p style={{ margin: '0' }}>Please arrive 10 minutes before your appointment</p>
                  <p style={{ margin: '4px 0 0 0' }}>Thank you for choosing {bookingReceipt.hospital_name}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4 no-print">
                <Button 
                  variant="outline"
                  onClick={() => setShowAppointmentReceipt(false)}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button 
                  onClick={() => {
                    // Create a new window for printing
                    const printWindow = window.open('', '_blank');
                    const receiptHTML = `
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Appointment Receipt</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
                          .receipt { max-width: 400px; margin: 0 auto; }
                          .header { text-align: center; padding-bottom: 15px; border-bottom: 1px dashed #ccc; margin-bottom: 15px; }
                          .header img { height: 50px; margin-bottom: 10px; }
                          .header h2 { margin: 0; font-size: 18px; }
                          .header p { margin: 5px 0; color: #666; font-size: 12px; }
                          .title { font-size: 14px; font-weight: 600; margin-top: 10px; }
                          .row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 13px; }
                          .row .label { color: #666; }
                          .row .value { font-weight: 500; }
                          .divider { height: 1px; background: #ddd; margin: 10px 0; }
                          .fee-row { display: flex; justify-content: space-between; padding: 15px 0; font-size: 15px; font-weight: bold; border-top: 1px solid #ddd; margin-top: 10px; }
                          .footer { text-align: center; font-size: 11px; color: #888; font-style: italic; margin-top: 20px; padding-top: 15px; border-top: 1px dashed #ccc; }
                          .green { color: #16a34a; }
                          .amber { color: #d97706; }
                          @media print { body { padding: 0; } }
                        </style>
                      </head>
                      <body>
                        <div class="receipt">
                          <div class="header">
                            ${bookingReceipt.hospital_logo ? `<img src="${bookingReceipt.hospital_logo.startsWith('http') ? bookingReceipt.hospital_logo : BACKEND_URL + bookingReceipt.hospital_logo}" alt="Logo" crossorigin="anonymous" />` : ''}
                            <h2>${bookingReceipt.hospital_name || ''}</h2>
                            ${bookingReceipt.hospital_address ? `<p>${bookingReceipt.hospital_address}</p>` : ''}
                            <p class="title">Appointment Receipt</p>
                          </div>
                          <div class="row"><span class="label">ID:</span><span class="value">${bookingReceipt.baby_id?.toUpperCase() || ''}</span></div>
                          <div class="row"><span class="label">Name:</span><span class="value">${bookingReceipt.baby_name || ''}</span></div>
                          <div class="row"><span class="label">Contact:</span><span class="value">${bookingReceipt.baby_phone || bookingReceipt.appointment_contact || 'N/A'}</span></div>
                          <div class="row"><span class="label">Doctor:</span><span class="value">${bookingReceipt.doctor_name || ''}</span></div>
                          ${bookingReceipt.specialization ? `<div class="row"><span class="label">Specialization:</span><span class="value">${bookingReceipt.specialization}</span></div>` : ''}
                          <div class="row"><span class="label">Date:</span><span class="value">${formatDateToDDMMYYYY(bookingReceipt.appointment_date)}</span></div>
                          <div class="row"><span class="label">Time:</span><span class="value">${bookingReceipt.appointment_time || ''}</span></div>
                          <div class="row"><span class="label">Visit Type:</span><span class="value ${bookingReceipt.is_free_visit ? 'green' : ''}">${bookingReceipt.appointment_type === 'followup' ? (bookingReceipt.is_free_visit ? 'Follow-Up (Free)' : 'Follow-Up') : 'New'}</span></div>
                          ${bookingReceipt.appointment_type === 'followup' && bookingReceipt.followup_of_date ? `<div class="row"><span class="label">Previous Visit:</span><span class="value">${bookingReceipt.followup_of_date}</span></div>` : ''}
                          <div class="row"><span class="label">Category:</span><span class="value ${bookingReceipt.category === 'Known' ? 'amber' : ''}">${bookingReceipt.category || 'Regular'}</span></div>
                          ${bookingReceipt.category === 'Known' && bookingReceipt.category_ref ? `<div class="row"><span class="label">Reference:</span><span class="value">${bookingReceipt.category_ref}</span></div>` : ''}
                          ${bookingReceipt.category !== 'Known' ? `<div class="row"><span class="label">Payment:</span><span class="value">${bookingReceipt.payment_method || 'Cash'}</span></div>` : ''}
                          <div class="fee-row">
                            <span>Consultation Fee:</span>
                            <span class="${bookingReceipt.consultation_fee === 0 ? 'green' : ''}">${bookingReceipt.consultation_fee === 0 ? '₹0 (Free)' : (bookingReceipt.consultation_fee > 0 ? '₹' + bookingReceipt.consultation_fee : 'To be confirmed')}</span>
                          </div>
                          <div class="footer">
                            <p>Please arrive 10 minutes before your appointment</p>
                            <p>Thank you for choosing ${bookingReceipt.hospital_name || 'us'}</p>
                          </div>
                        </div>
                        <script>window.onload = function() { window.print(); }</script>
                      </body>
                      </html>
                    `;
                    printWindow.document.write(receiptHTML);
                    printWindow.document.close();
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Print
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FrontDeskDashboard;
